import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIndustry, {useCachedIndustrySchemaController} from "../../../../../hooks/use-industry";
import {Box, Typography} from "@mui/material";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import useQuery from "../../../../../hooks/use-query";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import axios from "axios";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import LookupSearchViewWarehouseSelect from "./warehouse-select";
import LookupSearchViewTopbarSearch from "./top-bar-search";
import Grid from "@mui/material/Unstable_Grid2";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import LookupSearchViewProducts from "./products";
import LookupSearchViewSidebarSearch from "./sidebar-search";
import Button from "../../../../../components/base/button/button";
import BroadcastService from "../../../../../../core/services/broadcast";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Api from "../../../../../../core/services/api/api";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import CacheService from "../../../../../../core/services/cache/cache_service";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useSavedComponentsState from "../../../../../hooks/use-saved-components-state";
import {NexusSearchSchemaEntryNames} from "../../../../../../core/constants/enums";
import {usePartnerInformation} from "../../../../../hooks/use-partner-information";
import usePartnerDefaultNexusSearchOrderBy from "../../../../../hooks/use-partner-default-nexus-search-order-by";


export const initialLookupV2SearchData = {
    industryId: undefined,
    keyword: '',
    variance: undefined,
    filters: [],
    orderBy: undefined,
    /** Persists after `query.vin` is cleared by resetQueryKeyword so the decoder does not remount empty. */
    vin: undefined,
}

const LookupSearchView = () => {
    const {getSchemaForIndustry} = useCachedIndustrySchemaController();
    const [query, setQuery] = useQuery();
    const {
        cachedIndustry,
        loadingIndustries,
        selectIndustry,
        industries,
        hasNoIndustries,
    } = useIndustry({noCategories: true});
    const [searchData, setSearchData] = useState(initialLookupV2SearchData);
    const [featureFlags, setFeatureFlags] = useState(CacheService.getNexusSearchConfigs());
    const [searchSchema, setSearchSchema] = useState({entries: []});
    const [sidebarData, setSidebarData] = useState({searchFacets: []});
    const [loadingSearchData, setLoadingSearchData] = useState(true);
    const [loadingSearchSchema, setLoadingSearchSchema] = useState(true);
    const [loadingPartnerAndWarehouse, setLoadingPartnerAndWarehouse] = useState(true);
    const [advancedSearch, setAdvancedSearch] = useState(featureFlags.canToggleAdvancedMode);
    const dispatch = useDispatch();
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const needToLoadSearchDataFromQuery = useRef(!!query.searchToken);
    /** Raw GetSearchLog filters kept so variety can be restored after schema provides varietyFilterId. */
    const pendingSearchLogFiltersRef = useRef(null);
    const [selectedPartnerAndWarehouse, setSelectedPartnerAndWarehouse] = useState({partner: null, warehouses: null,});
    const partnerInformation = usePartnerInformation();
    const partnerId = useMemo(
        () => partnerInformation?.id ?? CacheService.getOperatorPartnerId(),
        [partnerInformation?.id],
    );

    const readonly = useMemo(() => query.readonly === 'true', [query.readonly])
    /** Prefer searchData.vin — it survives URL cleanup after the first search. */
    const initialVin = useMemo(
        () => (typeof searchData.vin === 'string' ? searchData.vin : (typeof query.vin === 'string' ? query.vin : '')),
        [searchData.vin, query.vin],
    );

    const {configureNotebookTags} = useConfigureNotebook('lookup-search-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {highestVersion: getCachedPartnerSelection, save: saveCachedPartnerSelection} = useSavedComponentsState({name: "nexus_search_cached_partner", version: '3.0.0'});
    const {highestVersion: getCachedWarehousesSelection, save: saveCachedWarehousesSelection} = useSavedComponentsState({name: "nexus_search_cached_warehouses", version: '3.0.0'});


    /**
     * As soon as the component mounts:
     * - listens to the broadcast channel for the search configs.
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(BroadcastService.channelNames.nexusSearchConfigs, 'message', setFeatureFlags)
        return () => {
            bcClose();
        }
    }, [setFeatureFlags])

    /**
     * Applies search criteria published from the app-bar Nexus field while this page is open
     * (keyword and/or advanced filters such as part number / tags).
     */
    useEffect(() => {
        const {close: bcClose} = BroadcastService.addEventListener(
            BroadcastService.channelNames.nexusSearchCriteria,
            'message',
            (payload) => {
                if (!payload || typeof payload !== 'object') {
                    return;
                }
                const keyword = typeof payload.keyword === 'string' ? payload.keyword : '';
                const filters = Array.isArray(payload.filters) ? payload.filters : [];
                const industryId = payload.industryId;

                setSearchData((prevState) => ({
                    ...prevState,
                    keyword,
                    filters,
                    industryId: industryId ?? prevState.industryId,
                    variance: undefined,
                }));
                setQuery((prevState) => ({
                    ...prevState,
                    keyword: undefined,
                    filters: undefined,
                    industryId: undefined,
                    variance: undefined,
                    vin: undefined,
                    searchToken: undefined,
                }), {replace: true});
            },
        );
        return () => {
            bcClose();
        };
    }, [setQuery])

    /**
     * Fetches the cached partner and warehouses.
     * @type {(function(): Promise<void>)}
     */
    const getCachedPartnerAndWarehouse = useCallback(async () => {
        setLoadingPartnerAndWarehouse(true);
        const [cachedPartnerSelection, cachedWarehousesSelection] = await Promise.all([
            getCachedPartnerSelection(),
            getCachedWarehousesSelection(),
        ]);
        if (!isMounted())
            return;
        if (cachedPartnerSelection?.data || cachedWarehousesSelection?.data) {
            setSelectedPartnerAndWarehouse({
                partner: cachedPartnerSelection?.data ?? null,
                warehouses: cachedWarehousesSelection?.data ?? null,
            })
        }
        setLoadingPartnerAndWarehouse(false);
    }, [getCachedPartnerSelection, getCachedWarehousesSelection, isMounted])

    /**
     * Saves the selected partner and warehouses.
     * @type {(function({partner: *, warehouses: *}): Promise<void>)}
     */
    const saveSelectedPartnerAndWarehouse = useCallback(async ({partner, warehouses}) => {
        await Promise.all([
            saveCachedPartnerSelection(partner),
            saveCachedWarehousesSelection(warehouses),
        ]);
    }, [saveCachedPartnerSelection, saveCachedWarehousesSelection])

    /**
     * As soon as the component mounts:
     * - fetches the cached partner and warehouses.
     */
    useEffect(() => {
        void getCachedPartnerAndWarehouse();
    }, [getCachedPartnerAndWarehouse])

    /**@type {string}*/
    const broadcaster = useMemo(() => query?.broadcaster, [query])

    const fromUserHomepageSearchQuery = useMemo(() => !!broadcaster, [broadcaster])
    const canBindSearchQuery = useMemo(() => {
        if (!fromUserHomepageSearchQuery)
            return false;
        return !!query.searchToken
    }, [fromUserHomepageSearchQuery, query.searchToken])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.lookupNexusSearch);
        configureBehavioralTool((tags) => tags.lookupNexusSearch);
    }, [configureBehavioralTool, configureNotebookTags]);

    const searchIndustryIdRef = useRef(searchData.industryId);
    searchIndustryIdRef.current = searchData.industryId;

    /**
     * Hydrates search state from the URL.
     * VIN deep-links clear prior keyword / filters / variance so the decoder can apply fresh filters.
     * When the industry changes via VIN, unload the stale schema immediately so filters are not
     * matched against the previous industry's entry ids.
     */
    useEffect(() => {
        if (query.vin) {
            const nextIndustryId = query.industryId;
            const industryChanging = Boolean(
                nextIndustryId
                && String(searchIndustryIdRef.current ?? '') !== String(nextIndustryId),
            );
            if (industryChanging) {
                setLoadingSearchSchema(true);
                setSearchSchema({entries: []});
                setSidebarData({searchFacets: []});
            }
            setSearchData((prevState) => ({
                ...prevState,
                vin: query.vin,
                keyword: '',
                filters: [],
                variance: undefined,
                ...(nextIndustryId ? {industryId: nextIndustryId} : {}),
            }));
            return;
        }
        setSearchData(prevState => {
            let newState = prevState;
            if (query.keyword) {
                newState = {...newState, keyword: query.keyword}
            }
            if (query.industryId) {
                newState = {...newState, industryId: query.industryId}
            }
            if (query.filters) {
                newState = {...newState, filters: JSON.parse(query.filters)}
            }
            if (query.variance) {
                newState = {...newState, variance: query.variance}
            }
            return newState;
        })
    }, [query.keyword, query.filters, query.industryId, query.variance, query.vin])

    /**
     * Sets the search token
     * @type {(function(*): void)}
     */
    const setSearchToken = useCallback((searchToken) => {
        // as soon as the keyword is replaced.
        setQuery(prevState => ({...prevState, searchToken: searchToken}), {replace: true})
    }, [setQuery])


    /**
     * Resets the query keywords
     * @type {(function(): void)}
     */
    const resetQueryKeyword = useCallback(() => {
        setQuery(prevState => ({
            ...prevState,
            keyword: undefined,
            filters: undefined,
            industryId: undefined,
            variance: undefined,
            vin: undefined,
        }), {replace: true})
    }, [setQuery])

    /**
     * Sets the order by of the search data.
     * @type {(function(*): void)}
     */
    const setOrderByRaw = useCallback((orderBy) => {
        setSearchData(prevState => ({...prevState, orderBy: orderBy}));
    }, [])

    const {
        setOrderBy,
        resetOrderByToDefault,
        resetSortInteraction,
        markSortInteraction,
        isOrderByReady,
    } = usePartnerDefaultNexusSearchOrderBy({
        partnerId,
        searchSchema,
        loadingSearchSchema,
        orderBy: searchData.orderBy,
        setOrderBy: setOrderByRaw,
        isTokenLoading: !!query.searchToken && loadingSearchData,
    })

    const resultsReady = !loadingSearchData && !loadingSearchSchema && isOrderByReady;

    /**
     * Changes the industry with a confirmation dialog.
     * @type {(function(*, *): void)}
     */
    const onTabChanged = useCallback((e, industryId) => {
        const applyChanges = (industry) => {
            selectIndustry(industry);
            resetSortInteraction();
            setLoadingSearchSchema(true);
            setQuery(prevState => ({
                ...prevState,
                keyword: undefined, filters: undefined, industryId: undefined, searchToken: undefined, variance: undefined, vin: undefined,
            }), {replace: true})
            setSearchData(({...initialLookupV2SearchData, industryId: industryId}))
            setSidebarData({searchFacets: []})
        }
        const industry = industries.find(e => e.id === industryId);
        if (!searchData.filters?.length && !searchData.keyword) {
            return applyChanges(industry);
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
            open: true,
            title: 'Change Industry',
            description: 'Are you sure you want to change the industry? this will reset the search filters and results',
            cancelButtonText: 'Cancel',
            confirmButtonText: 'Change',
            onClose: async (confirmed) => {
                if (confirmed) {
                    applyChanges(industry);
                }
                return true;
            }
        }));
    }, [selectIndustry, dispatch, industries, setQuery, searchData, resetSortInteraction])

    const varietyFilterId = useMemo(() => searchSchema.entries.find(s => s.name === NexusSearchSchemaEntryNames.variety)?.id ?? null, [searchSchema.entries]);
    const varietyFilterIdRef = useRef(varietyFilterId);
    varietyFilterIdRef.current = varietyFilterId;

    /**
     * Fetches the search data via the provided search token.
     * Intentionally independent of varietyFilterId so schema load does not cancel/restart GetSearchLog.
     * @type {(function(*, *): Promise<void>)}
     */
    const getSearchDataViaToken = useCallback(async (searchToken, cancelToken) => {
        setLoadingSearchData(true);
        const response = await Api.getNexusSearchLog(searchToken, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingSearchData(false);
        }
        if (response?.resultFlag && !response?.aborted) {
            needToLoadSearchDataFromQuery.current = false;
            const logFilters = response.data?.filters ?? [];
            const currentVarietyId = varietyFilterIdRef.current;
            const varianceFilter = currentVarietyId
                ? logFilters.find(e => e.industryIndexStructureId === currentVarietyId)
                : null;
            pendingSearchLogFiltersRef.current = currentVarietyId ? null : logFilters;
            const orderBy = logFilters.find(e => e.isForSorting);
            if (orderBy) {
                markSortInteraction();
            }
            setSearchData({
                industryId: response.data?.industryId,
                keyword: response.data?.keyword,
                filters: logFilters.filter(e => e.isForFiltering).map(e => ({
                    filterId: e.industryIndexStructureId,
                    value: e.value,
                })),
                variance: varianceFilter ? {variety: varianceFilter.value, partNo: undefined} : undefined,
                orderBy: orderBy
                    ? {id: orderBy.industryIndexStructureId, isDescending: orderBy.isDesc}
                    : null,
            })
        }
    }, [isMounted, markSortInteraction])

    /**
     * Fetches the search data via token if the token exists.
     * Does not wait on schema — waiting previously cleared loadingSearchData early and broke isTokenLoading.
     */
    useEffect(() => {
        if (!query.searchToken || !needToLoadSearchDataFromQuery.current) {
            setLoadingSearchData(false);
            return;
        }
        const axiosCancelTokenSource = axios.CancelToken.source();
        void getSearchDataViaToken(query.searchToken, axiosCancelTokenSource.token);
        return () => axiosCancelTokenSource.cancel();
    }, [getSearchDataViaToken, query.searchToken]);

    /**
     * Restores variance UI state from GetSearchLog once schema exposes the variety filter id.
     */
    useEffect(() => {
        if (!varietyFilterId || !pendingSearchLogFiltersRef.current) {
            return;
        }
        const logFilters = pendingSearchLogFiltersRef.current;
        pendingSearchLogFiltersRef.current = null;
        const varianceFilter = logFilters.find(e => e.industryIndexStructureId === varietyFilterId);
        if (!varianceFilter) {
            return;
        }
        setSearchData(prevState => ({
            ...prevState,
            variance: {variety: varianceFilter.value, partNo: undefined},
        }));
    }, [varietyFilterId]);

    /**
     * Sets the default industryId of the search data with the cached industry id if it exists
     */
    useEffect(() => {
        setSearchData(prevState => {
            if (!loadingIndustries && !searchData.industryId && cachedIndustry) {
                return ({...prevState, industryId: cachedIndustry.id})
            }
            return prevState;
        })
    }, [loadingIndustries, cachedIndustry, searchData.industryId]);


    /**
     * Fetches the search schema
     * @type {(function(*): Promise<void>)}
     */
    const getSearchSchema = useCallback(async (industryId) => {
        setLoadingSearchSchema(true);
        const schema = await getSchemaForIndustry(industryId);
        if (!isMounted())
            return;
        setLoadingSearchSchema(false);
        if (schema) {
            setSearchSchema(schema);
        }
    }, [isMounted, getSchemaForIndustry])

    /**
     * As soon as the component mounts, fetch the search schema.
     */
    useEffect(() => {
        if (!searchData.industryId)
            return;
        void getSearchSchema(searchData.industryId);
    }, [getSearchSchema, searchData.industryId])

    /**
     * Sets the search facets
     * @type {(function(*): void)}
     */
    const setSearchFacets = useCallback((facets) => {
        setSidebarData(prevState => ({...prevState, searchFacets: facets}))
    }, [])

    /**
     * Sets the filters of the search data.
     * @type {(function(*): void)}
     */
    const setFilters = useCallback((filters) => {
        setSearchData(prevState => ({...prevState, filters: filters}));
    }, [])

    /**
     * Binds the used search query to the home page search query that opened this window tab.
     * * opens a broadcaster, sends its message, and then closes it.
     */
    const bindSearchQueryToHomepageComponent = useCallback(() => {
        BroadcastService.postMessage(broadcaster, {
            nexusSearchId: query.searchToken,
        });
    }, [broadcaster, query.searchToken])

    /**
     * Sets the selected partner and warehouses.
     * @type {(function(*): void)}
     */
    const setPartnerAndWarehouse = useCallback((value) => {
        if (value) {
            void saveSelectedPartnerAndWarehouse({
                warehouses: value.warehouses,
                partner: {id: value.partnerId, name: value.partnerName,},
            });
        }
        setSelectedPartnerAndWarehouse({
            partner: value ? {id: value.partnerId, name: value.partnerName,} : null,
            warehouses: value ? value.warehouses : null,
        })
    }, [saveSelectedPartnerAndWarehouse])

    const LookupSearchViewWarehouseSelectValue = useMemo(() => {
        if (!selectedPartnerAndWarehouse.warehouses && !selectedPartnerAndWarehouse.partner)
            return null
        return {
            warehouses: selectedPartnerAndWarehouse.warehouses,
            partnerId: selectedPartnerAndWarehouse.partner?.id,
            partnerName: selectedPartnerAndWarehouse.partner?.name,
        };
    }, [selectedPartnerAndWarehouse.partner, selectedPartnerAndWarehouse.warehouses]);

    const currentIndustry = useMemo(() => industries.find((industry) => String(industry.id) === String(searchData.industryId)) ?? null, [industries, searchData.industryId]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography variant={'subtitle1'} color={'text.primary'} fontWeight={600}>
                        Nexus Search {
                        readonly &&
                        <Typography component={'span'} variant={'subtitle1'} color={'primary.main'} fontWeight={700}>
                            (Viewing a log)
                        </Typography>
                    }
                    </Typography>
                    {
                        readonly &&
                        <Button
                            color={'primary'}
                            variant={'outlined'}
                            onClick={() => setQuery(prevState => ({...prevState, readonly: undefined, searchToken: undefined}), {replace: true})}
                        >
                            Exit Log
                        </Button>
                    }
                    <Box sx={{flexGrow: 1}}/>
                    {
                        operatorAccessLevels.packages.bizInventory.availabilityInLookup &&
                        !loadingPartnerAndWarehouse &&
                        <LookupSearchViewWarehouseSelect
                            value={LookupSearchViewWarehouseSelectValue}
                            setValue={setPartnerAndWarehouse}
                        />
                    }
                </Box>
                {
                    industries.length > 1 &&
                    <Tabs value={searchData?.industryId ?? cachedIndustry?.id ?? false} onChange={onTabChanged} disabled={readonly}>
                        {
                            industries.map(industry => (
                                <Tab
                                    key={industry.id}
                                    value={industry.id}
                                    label={industry.title}
                                    disabled={readonly}
                                />
                            ))
                        }
                    </Tabs>
                }
                {
                    hasNoIndustries &&
                    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 12}}>
                        <Typography variant={'body2'} color={'text.secondary'} fontWeight={600} textAlign={'center'}>
                            No industries found. Please add an industry or contact your administrator.
                        </Typography>
                    </Box>
                }
                {
                    !hasNoIndustries &&
                    <>
                        <LookupSearchViewTopbarSearch
                            readonly={readonly}
                            loading={loadingSearchData || loadingSearchSchema || !isOrderByReady}
                            searchData={searchData}
                            setSearchData={setSearchData}
                            searchSchema={searchSchema}
                            searchToken={query.searchToken}
                            advancedSearch={advancedSearch}
                            setAdvancedSearch={setAdvancedSearch}
                            featureFlags={featureFlags}
                            resetOrderByToDefault={resetOrderByToDefault}
                        />
                        {
                            !resultsReady &&
                            <Box
                                sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginBlock: 12}}>
                                <LoadingLottieAnimation width={'80%'}/>
                            </Box>
                        }
                        {
                            resultsReady &&
                            <Grid container>
                                <Grid xs={12} md={'auto'}>
                                    <Box sx={{width: {xs: '100%', md: 300}}}>
                                        <LookupSearchViewSidebarSearch
                                            currentIndustry={currentIndustry}
                                            initialVin={initialVin}
                                            readonly={readonly}
                                            featureFlags={featureFlags}
                                            searchSchema={searchSchema}
                                            facets={sidebarData.searchFacets}
                                            filters={searchData.filters}
                                            orderBy={searchData.orderBy}
                                            setOrderBy={setOrderBy}
                                            setFilters={setFilters}
                                        />
                                    </Box>
                                </Grid>
                                <Grid xs={12} md sx={{display: 'flex', flexDirection: 'column'}}>
                                    {
                                        canBindSearchQuery &&
                                        <div className={'d-flex justify-content-center w-100 my-4'}>
                                            <Button
                                                color={'primary'}
                                                variant={'contained'}
                                                className='px-8 mx-auto my-4'
                                                onClick={bindSearchQueryToHomepageComponent}
                                            >
                                                Bind the search result to the more button.
                                            </Button>
                                        </div>
                                    }
                                    {
                                        readonly &&
                                        <div className={'d-flex justify-content-center w-100 my-5'}>
                                            <Typography variant={'body2'} fontWeight={500} textAlign={'center'}>
                                                This search is from a search log. If you want to search again, please click on the "Exit Log" button at the top.
                                            </Typography>
                                        </div>
                                    }
                                    <LookupSearchViewProducts
                                        readonly={readonly}
                                        industries={industries}
                                        advancedSearch={advancedSearch && featureFlags.canToggleAdvancedMode}
                                        searchData={searchData}
                                        selectedPartner={selectedPartnerAndWarehouse.partner}
                                        selectedWarehouses={selectedPartnerAndWarehouse.warehouses}
                                        minRelevanceScore={searchSchema.minRelevanceScore ?? 0}
                                        setSearchToken={setSearchToken}
                                        searchToken={query.searchToken}
                                        setFacets={setSearchFacets}
                                        resetQueryKeyword={resetQueryKeyword}
                                        searchSchema={searchSchema}
                                        setSearchData={setSearchData}
                                        getScrollParent={() => document.querySelector('main')}
                                    />
                                </Grid>
                            </Grid>
                        }
                    </>
                }
            </Box>
        </>
    );
}


export default LookupSearchView;
